import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Pagination,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
  Divider,
  Tabs,
  Tab,
  InputAdornment,
  TablePagination,
  Avatar,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Flag as FlagIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Settings as SettingsIcon,
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  AccountCircle as AccountCircleIcon,
  Warning as WarningIcon,
  History as HistoryIcon,
  VerifiedUser as VerifiedUserIcon,
  Star as StarIcon,
  Support as SupportIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Security as SecurityIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';
import API from '../config';
import { AuthContext } from '../../contexts/AuthContext';

const isPromoExpired = (promo) => Boolean(
  promo.expiresAt && new Date(promo.expiresAt).getTime() <= Date.now()
);

const getAdminAccessTier = (user) => {
  if (user.accessTier) return user.accessTier;
  return ['active', 'cancel_at_period_end', 'trialing', 'past_due'].includes(user.subscriptionStatus)
    ? 'paid'
    : 'inactive';
};

const PROMO_EXPIRATION_TIMES = [
  ['09:00', '9:00 AM'],
  ['12:00', '12:00 PM'],
  ['15:00', '3:00 PM'],
  ['18:00', '6:00 PM'],
  ['21:00', '9:00 PM'],
  ['23:59', '11:59 PM'],
];

export default function AdminReportsDashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { authData } = useContext(AuthContext);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('pending');
  
  // Selected report for actions
  const [selectedReport, setSelectedReport] = useState(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(null); // 'approve' or 'dismiss'
  const [resolutionNote, setResolutionNote] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // Review details dialog
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  
  // Delete review confirmation dialog
  const [deleteReviewDialogOpen, setDeleteReviewDialogOpen] = useState(false);
  const [reviewToDelete, setReviewToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  
  // Tab state
  const [currentTab, setCurrentTab] = useState(0); // 0 = Reports, 1 = Users, 2 = Settings, 3 = Support Tickets, 4 = Developer
  
  // User management state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersPage, setUsersPage] = useState(0);
  const [usersRowsPerPage, setUsersRowsPerPage] = useState(20);
  const [usersTotal, setUsersTotal] = useState(0);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userSortOrder, setUserSortOrder] = useState('newest');
  
  // Admin invitation state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  
  // Subscription settings state
  const [subscriptionSettings, setSubscriptionSettings] = useState({
    yearly_enabled: false,
  });
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [confirmSettingsDialogOpen, setConfirmSettingsDialogOpen] = useState(false);
  const [pendingSettings, setPendingSettings] = useState(null);
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoForm, setPromoForm] = useState({ code: '', label: '', durationDays: 7, maxRedemptions: '', expiresAt: '', expiresAtTime: '23:59' });
  const [creatingPromo, setCreatingPromo] = useState(false);
  const [promoToDelete, setPromoToDelete] = useState(null);
  const [deletingPromo, setDeletingPromo] = useState(false);
  const [promoAnalytics, setPromoAnalytics] = useState(null);
  const [promoAnalyticsOpen, setPromoAnalyticsOpen] = useState(false);
  const [promoAnalyticsLoading, setPromoAnalyticsLoading] = useState(false);
  
  // Delete user confirmation dialog
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteUserConfirmText, setDeleteUserConfirmText] = useState('');
  
  // Block user confirmation dialog
  const [blockUserDialogOpen, setBlockUserDialogOpen] = useState(false);
  const [userToBlock, setUserToBlock] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [blockUserConfirmEmail, setBlockUserConfirmEmail] = useState('');
  
  // Blocklist state
  const [blocklist, setBlocklist] = useState([]);
  const [blocklistLoading, setBlocklistLoading] = useState(false);
  const [blocklistPage, setBlocklistPage] = useState(1);
  const [blocklistTotalPages, setBlocklistTotalPages] = useState(1);
  const [blocklistTotal, setBlocklistTotal] = useState(0);
  const [blocklistSearch, setBlocklistSearch] = useState('');
  
  // User activity dialog
  const [userActivityDialogOpen, setUserActivityDialogOpen] = useState(false);
  const [selectedUserActivity, setSelectedUserActivity] = useState(null);
  const [loadingActivity, setLoadingActivity] = useState(false);
  
  // Support tickets state
  const [supportTickets, setSupportTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsPage, setTicketsPage] = useState(1);
  const [ticketsTotalPages, setTicketsTotalPages] = useState(1);
  const [ticketsTotal, setTicketsTotal] = useState(0);
  const [ticketsStatusFilter, setTicketsStatusFilter] = useState('active');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketStatusDialogOpen, setTicketStatusDialogOpen] = useState(false);
  const [ticketStatusNote, setTicketStatusNote] = useState('');
  const [ticketAdminNotes, setTicketAdminNotes] = useState('');
  const [updatingTicketStatus, setUpdatingTicketStatus] = useState(false);
  
  // Security logs state
  const [securityLogs, setSecurityLogs] = useState([]);
  const [securityLogsLoading, setSecurityLogsLoading] = useState(false);
  const [securityLogsPage, setSecurityLogsPage] = useState(1);
  const [securityLogsTotalPages, setSecurityLogsTotalPages] = useState(1);
  const [securityLogsTotal, setSecurityLogsTotal] = useState(0);
  const [securityLogsSeverityFilter, setSecurityLogsSeverityFilter] = useState('all');
  const [securityLogsActivityFilter, setSecurityLogsActivityFilter] = useState('all');
  const [securityLogsDays, setSecurityLogsDays] = useState(7);
  const [securityStats, setSecurityStats] = useState(null);
  const [securityLogReviewsDialogOpen, setSecurityLogReviewsDialogOpen] = useState(false);
  const [selectedSecurityLogReviews, setSelectedSecurityLogReviews] = useState(null);

  useEffect(() => {
    // Check if user is admin (you might want to add an API endpoint for this)
    if (!authData?.access_token) {
      navigate('/login');
      return;
    }
    
    fetchReports();
    fetchStats();
    
    // Auto-refresh stats every 30 seconds to show new pending reports
    const statsInterval = setInterval(() => {
      fetchStats();
    }, 30000);
    
    return () => clearInterval(statsInterval);
  }, [page, statusFilter, authData]);

  // Fetch users when on Users tab
  useEffect(() => {
    if (currentTab === 1) {
      fetchUsers();
      fetchBlocklist();
    }
    if (currentTab === 3) {
      fetchSupportTickets();
    }
  }, [currentTab, usersPage, usersRowsPerPage, userSearchTerm, userSortOrder, ticketsPage, ticketsStatusFilter, blocklistPage, blocklistSearch]);

  // Fetch subscription settings when on Settings tab
  useEffect(() => {
    if (currentTab === 2) {
      fetchSubscriptionSettings();
      fetchPromoCodes();
    }
  }, [currentTab]);

  // Fetch security logs when on Developer tab
  useEffect(() => {
    if (currentTab === 4) {
      fetchSecurityLogs();
      fetchSecurityStats();
    }
  }, [currentTab, securityLogsPage, securityLogsSeverityFilter, securityLogsActivityFilter, securityLogsDays]);

  const fetchSubscriptionSettings = async () => {
    setLoadingSettings(true);
    try {
      const response = await api.get(`${API}/api/admin/subscription-settings`);
      setSubscriptionSettings({
        yearly_enabled: response.data.yearly_enabled || false,
      });
    } catch (err) {
      console.error('Error fetching subscription settings:', err);
      toast.error('Failed to load subscription settings');
    } finally {
      setLoadingSettings(false);
    }
  };

  const fetchPromoCodes = async () => {
    try {
      const response = await api.get(`${API}/api/admin/promo-codes`);
      setPromoCodes(response.data.promoCodes || []);
    } catch (err) {
      console.error('Error fetching promo codes:', err);
      toast.error('Failed to load promo codes');
    }
  };

  const handleCreatePromo = async () => {
    setCreatingPromo(true);
    try {
      const response = await api.post(`${API}/api/admin/promo-codes`, {
        ...promoForm,
        maxRedemptions: promoForm.maxRedemptions || null,
        expiresAt: promoForm.expiresAt
          ? new Date(`${promoForm.expiresAt}T${promoForm.expiresAtTime}`).toISOString()
          : null,
      });
      setPromoCodes(current => [response.data.promoCode, ...current]);
      setPromoForm({ code: '', label: '', durationDays: 7, maxRedemptions: '', expiresAt: '', expiresAtTime: '23:59' });
      toast.success(`Promo code ${response.data.promoCode.code} created`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create promo code');
    } finally {
      setCreatingPromo(false);
    }
  };

  const handleTogglePromo = async (promo) => {
    try {
      const response = await api.patch(`${API}/api/admin/promo-codes/${promo._id}`, { active: !promo.active });
      setPromoCodes(current => current.map(item => item._id === promo._id ? { ...item, ...response.data.promoCode } : item));
      toast.success(`Promo code ${response.data.promoCode.active ? 'activated' : 'deactivated'}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update promo code');
    }
  };

  const handleDeletePromo = async () => {
    if (!promoToDelete) return;
    setDeletingPromo(true);
    try {
      await api.delete(`${API}/api/admin/promo-codes/${promoToDelete._id}`);
      setPromoCodes(current => current.filter(item => item._id !== promoToDelete._id));
      toast.success(`Promo code ${promoToDelete.code} deleted`);
      setPromoToDelete(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete promo code');
    } finally {
      setDeletingPromo(false);
    }
  };

  const handleViewPromoAnalytics = async (promo) => {
    setPromoAnalyticsOpen(true);
    setPromoAnalyticsLoading(true);
    setPromoAnalytics({ promoCode: promo, accounts: [], summary: null });
    try {
      const response = await api.get(`${API}/api/admin/promo-codes/${promo._id}/redemptions`);
      setPromoAnalytics(response.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load promo results');
      setPromoAnalyticsOpen(false);
    } finally {
      setPromoAnalyticsLoading(false);
    }
  };

  const handleSaveSubscriptionSettings = () => {
    // Show confirmation modal first
    setPendingSettings(subscriptionSettings);
    setConfirmSettingsDialogOpen(true);
  };

  const handleConfirmSaveSettings = async () => {
    if (!pendingSettings) return;
    
    setConfirmSettingsDialogOpen(false);
    setSavingSettings(true);
    try {
      const payload = {
        yearly_enabled: pendingSettings.yearly_enabled,
      };
      const response = await api.put(`${API}/api/admin/subscription-settings`, payload);
      toast.success('Subscription settings updated successfully');
      setSubscriptionSettings({
        yearly_enabled: response.data.yearly_enabled,
      });
      setPendingSettings(null);
    } catch (err) {
      console.error('Error updating subscription settings:', err);
      toast.error(err.response?.data?.error || 'Failed to update subscription settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await api.get(`${API}/api/admin/users`, {
        params: {
          page: usersPage + 1,
          limit: usersRowsPerPage,
          search: userSearchTerm,
          sort: userSortOrder,
        },
      });
      
      setUsers(response.data.users || []);
      setUsersTotal(response.data.total || 0);
    } catch (err) {
      console.error('Error fetching users:', err);
      toast.error('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleInviteAdmin = async () => {
    if (!inviteEmail || !inviteEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setInviting(true);
    try {
      const response = await api.post(`${API}/api/admin/invite-admin`, {
        email: inviteEmail,
      });
      
      toast.success(response.data.message || 'Admin privileges granted successfully');
      setInviteEmail('');
      setInviteDialogOpen(false);
      // Refresh users list if on Users tab
      if (currentTab === 1) {
        fetchUsers();
      }
    } catch (err) {
      console.error('Error inviting admin:', err);
      toast.error(err.response?.data?.error || 'Failed to invite admin');
    } finally {
      setInviting(false);
    }
  };

  const handleDeleteUserClick = (userId, userEmail) => {
    setUserToDelete({ id: userId, email: userEmail });
    setDeleteUserConfirmText('');
    setDeleteUserDialogOpen(true);
  };

  const handleBlockUserClick = (userId, userEmail, username) => {
    setUserToBlock({ id: userId, email: userEmail, username: username });
    setBlockReason('');
    setBlockUserConfirmEmail('');
    setBlockUserDialogOpen(true);
  };

  const handleBlockUserConfirm = async () => {
    if (!userToBlock) return;
    
    // Verify email matches
    if (blockUserConfirmEmail.toLowerCase() !== userToBlock.email.toLowerCase()) {
      toast.error('Email does not match. Please enter the user\'s email to confirm.');
      return;
    }
    
    setProcessing(true);
    try {
      await api.post(`${API}/api/admin/users/${userToBlock.id}/block`, {
        reason: blockReason
      });
      
      toast.success(`User ${userToBlock.email} has been blocked and their account deleted`);
      setBlockUserDialogOpen(false);
      setUserToBlock(null);
      setBlockReason('');
      setBlockUserConfirmEmail('');
      await fetchUsers();
      await fetchBlocklist(); // Refresh blocklist
    } catch (err) {
      console.error('Error blocking user:', err);
      toast.error(err.response?.data?.error || 'Failed to block user');
    } finally {
      setProcessing(false);
    }
  };

  const fetchBlocklist = async () => {
    setBlocklistLoading(true);
    try {
      const params = {
        page: blocklistPage,
        limit: 50,
      };
      
      if (blocklistSearch) {
        params.search = blocklistSearch;
      }
      
      const response = await api.get(`${API}/api/admin/blocklist`, { params });
      
      setBlocklist(response.data.blocked_emails || []);
      setBlocklistTotal(response.data.total || 0);
      setBlocklistTotalPages(response.data.totalPages || 1);
    } catch (err) {
      console.error('Error fetching blocklist:', err);
      toast.error('Failed to load blocklist');
    } finally {
      setBlocklistLoading(false);
    }
  };

  const handleUnblockEmail = async (blockId, email) => {
    if (!window.confirm(`Are you sure you want to unblock ${email}?`)) {
      return;
    }
    
    try {
      await api.delete(`${API}/api/admin/blocklist/${blockId}`);
      toast.success(`${email} has been unblocked`);
      await fetchBlocklist();
    } catch (err) {
      console.error('Error unblocking email:', err);
      toast.error(err.response?.data?.error || 'Failed to unblock email');
    }
  };

  const handleViewUserActivity = async (userId) => {
    setLoadingActivity(true);
    setUserActivityDialogOpen(true);
    try {
      const response = await api.get(`${API}/api/admin/users/${userId}/activity`);
      setSelectedUserActivity(response.data);
    } catch (err) {
      console.error('Error fetching user activity:', err);
      toast.error('Failed to load user activity');
      setUserActivityDialogOpen(false);
    } finally {
      setLoadingActivity(false);
    }
  };

  const handleDeleteUserConfirm = async () => {
    if (deleteUserConfirmText.toLowerCase() !== 'delete') {
      toast.error('Please type "delete" to confirm');
      return;
    }
    
    if (!userToDelete) return;
    
    setProcessing(true);
    try {
      await api.delete(`${API}/api/admin/users/${userToDelete.id}`);
      toast.success('User deleted successfully');
      setDeleteUserDialogOpen(false);
      setUserToDelete(null);
      setDeleteUserConfirmText('');
      // Refresh users list
      fetchUsers();
      // Refresh stats
      fetchStats();
    } catch (err) {
      console.error('Error deleting user:', err);
      toast.error(err.response?.data?.error || 'Failed to delete user');
    } finally {
      setProcessing(false);
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`${API}/api/admin/reports`, {
        params: {
          status: statusFilter,
          page,
          limit,
        },
      });
      
      setReports(response.data.reports || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.totalPages || 1);
    } catch (err) {
      console.error('Error fetching reports:', err);
      if (err.response?.status === 403) {
        setError('You do not have admin access.');
        toast.error('Admin access required');
      } else {
        setError('Failed to load reports. Please try again.');
        toast.error('Failed to load reports');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get(`${API}/api/admin/stats`);
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(1); // Reset to first page when filter changes
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleApprove = (report) => {
    setSelectedReport(report);
    setActionType('approve');
    setResolutionNote('');
    setActionDialogOpen(true);
  };

  const handleDismiss = (report) => {
    setSelectedReport(report);
    setActionType('dismiss');
    setResolutionNote('');
    setActionDialogOpen(true);
  };

  const handleDeleteReviewClick = (reviewId) => {
    setReviewToDelete(reviewId);
    setDeleteConfirmText('');
    setDeleteReviewDialogOpen(true);
  };

  const handleDeleteReviewConfirm = async () => {
    if (deleteConfirmText.toLowerCase() !== 'delete') {
      toast.error('Please type "delete" to confirm');
      return;
    }
    
    if (!reviewToDelete) return;
    
    setProcessing(true);
    try {
      await api.delete(`${API}/api/admin/reviews/${reviewToDelete}`);
      toast.success('Review deleted successfully');
      setDeleteReviewDialogOpen(false);
      setReviewToDelete(null);
      setDeleteConfirmText('');
      // Refresh both reports and stats
      await fetchReports();
      await fetchStats();
    } catch (err) {
      console.error('Error deleting review:', err);
      toast.error(err.response?.data?.error || 'Failed to delete review');
    } finally {
      setProcessing(false);
    }
  };

  const handleResolveAction = async () => {
    if (!selectedReport) return;
    
    setProcessing(true);
    try {
      await api.put(`${API}/api/admin/reports/${selectedReport._id}/resolve`, {
        action: actionType,
        resolution_note: resolutionNote,
      });
      
      toast.success(`Report ${actionType}d successfully`);
      setActionDialogOpen(false);
      setSelectedReport(null);
      setResolutionNote('');
      // Refresh both reports and stats
      await fetchReports();
      await fetchStats();
    } catch (err) {
      console.error('Error resolving report:', err);
      toast.error(err.response?.data?.error || 'Failed to resolve report');
    } finally {
      setProcessing(false);
    }
  };

  const handleViewReview = (review) => {
    setSelectedReview(review);
    setReviewDialogOpen(true);
  };

  const fetchSupportTickets = async () => {
    setTicketsLoading(true);
    try {
      const response = await api.get(`${API}/api/admin/support-tickets`, {
        params: {
          status: ticketsStatusFilter,
          page: ticketsPage,
          limit: 20,
        },
      });
      
      setSupportTickets(response.data.tickets || []);
      setTicketsTotal(response.data.total || 0);
      setTicketsTotalPages(response.data.totalPages || 1);
    } catch (err) {
      console.error('Error fetching support tickets:', err);
      toast.error('Failed to load support tickets');
    } finally {
      setTicketsLoading(false);
    }
  };

  const handleUpdateTicketStatus = (ticket, newStatus) => {
    setSelectedTicket(ticket);
    setTicketStatusNote(ticket.resolution_note || '');
    setTicketAdminNotes(ticket.admin_notes || '');
    setTicketStatusDialogOpen(true);
    // Store the new status we want to set
    setSelectedTicket({ ...ticket, pendingStatus: newStatus });
  };

  const fetchSecurityLogs = async () => {
    setSecurityLogsLoading(true);
    try {
      const params = {
        page: securityLogsPage,
        limit: 50,
        days: securityLogsDays,
      };
      
      if (securityLogsSeverityFilter !== 'all') {
        params.severity = securityLogsSeverityFilter;
      }
      
      if (securityLogsActivityFilter !== 'all') {
        params.activity_type = securityLogsActivityFilter;
      }
      
      const response = await api.get(`${API}/api/admin/security-logs`, { params });
      
      setSecurityLogs(response.data.logs || []);
      setSecurityLogsTotal(response.data.total || 0);
      setSecurityLogsTotalPages(response.data.totalPages || 1);
    } catch (err) {
      console.error('Error fetching security logs:', err);
      toast.error('Failed to load security logs');
    } finally {
      setSecurityLogsLoading(false);
    }
  };

  const fetchSecurityStats = async () => {
    try {
      const response = await api.get(`${API}/api/admin/security-stats`);
      setSecurityStats(response.data);
    } catch (err) {
      console.error('Error fetching security stats:', err);
    }
  };

  const handleConfirmTicketStatusUpdate = async () => {
    if (!selectedTicket) return;
    
    setUpdatingTicketStatus(true);
    try {
      const newStatus = selectedTicket.pendingStatus || selectedTicket.status;
      await api.put(`${API}/api/admin/support-tickets/${selectedTicket._id}/update-status`, {
        status: newStatus,
        resolution_note: ticketStatusNote,
        admin_notes: ticketAdminNotes,
      });
      
      toast.success(`Ticket status updated to ${newStatus}`);
      setTicketStatusDialogOpen(false);
      setSelectedTicket(null);
      setTicketStatusNote('');
      setTicketAdminNotes('');
      await fetchSupportTickets();
    } catch (err) {
      console.error('Error updating ticket status:', err);
      toast.error(err.response?.data?.error || 'Failed to update ticket status');
    } finally {
      setUpdatingTicketStatus(false);
    }
  };

  const capitalizeFirst = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const getReasonColor = (reason) => {
    const colors = {
      spam: 'error',
      inappropriate: 'warning',
      fake: 'error',
      harassment: 'error',
      other: 'default',
    };
    return colors[reason] || 'default';
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      resolved: 'success',
      dismissed: 'default',
    };
    return colors[status] || 'default';
  };

  if (error && error.includes('admin access')) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Typography variant="h4" fontWeight={700} sx={{ color: 'primary.main' }}>
            Admin Dashboard
          </Typography>
          {stats && stats.pending_reports > 0 && (
            <Chip
              label={`${stats.pending_reports} Pending${stats.pending_reports > 1 ? ' Reports' : ' Report'}`}
              color="error"
              sx={{
                fontSize: '0.875rem',
                fontWeight: 700,
                height: 32,
                animation: 'pulse 2s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': {
                    opacity: 1,
                  },
                  '50%': {
                    opacity: 0.7,
                  },
                },
              }}
            />
          )}
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
          Manage reports, users, and system settings
        </Typography>
      </Box>

      {/* Tabs */}
      <Paper sx={{ 
        mb: 3,
        borderRadius: 3,
        boxShadow: 2,
      }}>
        <Tabs 
          value={currentTab} 
          onChange={(e, newValue) => setCurrentTab(newValue)}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              minHeight: 64,
            },
          }}
        >
          <Tab icon={<FlagIcon />} label="Reports" iconPosition="start" />
          <Tab icon={<PeopleIcon />} label="Users" iconPosition="start" />
          <Tab icon={<SettingsIcon />} label="Settings" iconPosition="start" />
          <Tab icon={<SupportIcon />} label="Support Tickets" iconPosition="start" />
          <Tab icon={<SecurityIcon />} label="Developer" iconPosition="start" />
        </Tabs>
      </Paper>


      {/* Tab Panels */}
      {/* Reports Tab */}
      {currentTab === 0 && (
        <Box>
          {/* Report Stats Cards */}
          {stats && (
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  borderRadius: 3, 
                  boxShadow: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <FlagIcon sx={{ color: 'warning.main', fontSize: 28 }} />
                      <Typography variant="h4" fontWeight={700} color="warning.main">
                        {stats.pending_reports}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      Pending Reports
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  borderRadius: 3, 
                  boxShadow: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <CheckCircleIcon sx={{ color: 'success.main', fontSize: 28 }} />
                      <Typography variant="h4" fontWeight={700} color="success.main">
                        {stats.resolved_reports}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      Resolved Reports
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  borderRadius: 3, 
                  boxShadow: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <DeleteIcon sx={{ color: 'error.main', fontSize: 28 }} />
                      <Typography variant="h4" fontWeight={700} color="error.main">
                        {stats.hidden_reviews}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      Hidden Reviews
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  borderRadius: 3, 
                  boxShadow: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <FlagIcon sx={{ color: 'info.main', fontSize: 28 }} />
                      <Typography variant="h4" fontWeight={700} color="info.main">
                        {stats.total_reviews || 0}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      Total Reviews
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

      {/* Filters and Actions */}
      <Paper sx={{ 
        p: 2.5, 
        mb: 3,
        borderRadius: 3,
        boxShadow: 2,
      }}>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Status Filter</InputLabel>
            <Select
              value={statusFilter}
              label="Status Filter"
              onChange={handleStatusFilterChange}
              sx={{
                borderRadius: 2,
              }}
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="dismissed">Dismissed</MenuItem>
            </Select>
          </FormControl>
          
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => {
              fetchReports();
              fetchStats();
            }}
            disabled={loading}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Refresh
          </Button>
        </Stack>
      </Paper>

      {/* Reports Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : reports.length === 0 ? (
        <Paper sx={{ 
          p: 4, 
          textAlign: 'center',
          borderRadius: 3,
          boxShadow: 2,
        }}>
          <FlagIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No {statusFilter} reports found
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Review</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Creator</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Reporter</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Comment</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                    {reports.map((report) => (
                      <TableRow 
                        key={report._id} 
                        hover
                        sx={{
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                    <TableCell>
                      {report.review ? (
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {report.review.fullReview?.substring(0, 50)}...
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Quality: {report.review.contentQuality}/5
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          Review deleted
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {report.review?.creator_info?.username || 'Unknown'}
                    </TableCell>
                    <TableCell>
                      {report.reporter?.email || 'Unknown'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={capitalizeFirst(report.reason)}
                        size="small"
                        color={getReasonColor(report.reason)}
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      {report.details ? (
                        <Tooltip title={report.details}>
                          <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {report.details}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          No comment
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={capitalizeFirst(report.status)}
                        size="small"
                        color={getStatusColor(report.status)}
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      {report.createdAt
                        ? new Date(report.createdAt).toLocaleDateString()
                        : 'N/A'}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        {report.review && (
                          <>
                            <Tooltip title="View Review">
                              <IconButton
                                size="small"
                                onClick={() => handleViewReview(report.review)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {report.status === 'pending' && (
                              <>
                                <Tooltip title="Approve (Hide Review)">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleApprove(report)}
                                  >
                                    <CheckCircleIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Dismiss Report">
                                  <IconButton
                                    size="small"
                                    color="default"
                                    onClick={() => handleDismiss(report)}
                                  >
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete Review">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteReviewClick(report.review._id)}
                                    disabled={processing}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Paper sx={{ 
              mt: 3, 
              p: 2,
              borderRadius: 3,
              boxShadow: 2,
              display: 'flex',
              justifyContent: 'center',
            }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Paper>
          )}
        </>
      )}

      {/* Action Dialog */}
      <Dialog
        open={actionDialogOpen}
        onClose={() => !processing && setActionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {actionType === 'approve' ? 'Approve Report' : 'Dismiss Report'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {actionType === 'approve'
              ? 'This will hide the review and mark all related reports as resolved. The reporter will be notified.'
              : 'This will dismiss the report without taking action on the review. The reporter will be notified.'}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Resolution Note (Optional)"
            placeholder="Add a note about this resolution..."
            value={resolutionNote}
            onChange={(e) => setResolutionNote(e.target.value)}
            disabled={processing}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)} disabled={processing}>
            Cancel
          </Button>
          <Button
            onClick={handleResolveAction}
            variant="contained"
            color={actionType === 'approve' ? 'success' : 'default'}
            disabled={processing}
          >
            {processing ? 'Processing...' : actionType === 'approve' ? 'Approve' : 'Dismiss'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review Details Dialog */}
      <Dialog
        open={reviewDialogOpen}
        onClose={() => setReviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Review Details</DialogTitle>
        <DialogContent dividers>
          {selectedReview && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Creator: {selectedReview.creator_info?.username || 'Unknown'}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body1" sx={{ whiteSpace: 'pre-line', mb: 2 }}>
                {selectedReview.fullReview}
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Content Quality
                  </Typography>
                  <Typography variant="h6">{selectedReview.contentQuality}/5</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Recommended
                  </Typography>
                  <Typography variant="h6">
                    {selectedReview.recommend ? 'Yes' : 'No'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Verified
                  </Typography>
                  <Chip
                    label={selectedReview.verified ? 'Verified' : 'Unverified'}
                    size="small"
                    color={selectedReview.verified ? 'success' : 'default'}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Hidden
                  </Typography>
                  <Chip
                    label={selectedReview.is_hidden ? 'Hidden' : 'Visible'}
                    size="small"
                    color={selectedReview.is_hidden ? 'error' : 'success'}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
        </Box>
      )}

      {/* Users Tab */}
      {currentTab === 1 && (
        <Box>
          {/* User Stats Cards */}
          {stats && (
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  borderRadius: 3, 
                  boxShadow: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <PeopleIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                      <Typography variant="h4" fontWeight={700} color="primary.main">
                        {stats.signups_today || 0}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      Signups Today
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {stats.signups_this_week || 0} this week
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  borderRadius: 3, 
                  boxShadow: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <TrendingUpIcon sx={{ color: 'secondary.main', fontSize: 28 }} />
                      <Typography variant="h4" fontWeight={700} color="secondary.main">
                        {stats.vip_upgrades_today || 0}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      VIP Upgrades Today
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {stats.vip_upgrades_this_week || 0} this week
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          <Paper sx={{ 
            p: 3, 
            mb: 3,
            borderRadius: 3,
            boxShadow: 2,
          }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" fontWeight={700}>
                User Management
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                placeholder="Search by email or username..."
                value={userSearchTerm}
                onChange={(e) => {
                  setUserSearchTerm(e.target.value);
                  setUsersPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={userSortOrder}
                  label="Sort By"
                  onChange={(e) => {
                    setUserSortOrder(e.target.value);
                    setUsersPage(0);
                  }}
                  sx={{
                    borderRadius: 2,
                  }}
                >
                  <MenuItem value="newest">Newest First</MenuItem>
                  <MenuItem value="oldest">Oldest First</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Paper>

          {usersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : users.length === 0 ? (
            <Paper sx={{ 
              p: 4, 
              textAlign: 'center',
              borderRadius: 3,
              boxShadow: 2,
            }}>
              <PeopleIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No users found
              </Typography>
            </Paper>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Username</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Subscription</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Joined</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow 
                        key={user._id} 
                        hover
                        sx={{
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.username}</TableCell>
                        <TableCell>
                          {user.is_admin ? (
                            <Chip label="Admin" color="primary" size="small" sx={{ fontWeight: 600 }} />
                          ) : (
                            <Chip label="User" size="small" />
                          )}
                        </TableCell>
                        <TableCell>
                          {user.verified ? (
                            <Chip label="Verified" color="success" size="small" sx={{ fontWeight: 600 }} />
                          ) : (
                            <Chip label="Unverified" size="small" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getAdminAccessTier(user) === 'free'
                              ? 'Free'
                              : getAdminAccessTier(user) === 'paid'
                                ? 'Paid'
                                : getAdminAccessTier(user) === 'free_expired'
                                  ? 'Free expired'
                                  : 'Inactive'}
                            color={getAdminAccessTier(user) === 'paid'
                              ? 'success'
                              : getAdminAccessTier(user) === 'free'
                                ? 'info'
                                : 'default'}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                          {getAdminAccessTier(user) === 'free' && user.promoCode && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                              {user.promoCode}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {user.createdAt
                            ? (() => {
                                try {
                                  const date = new Date(user.createdAt);
                                  return date.toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  });
                                } catch (e) {
                                  return user.createdAt;
                                }
                              })()
                            : 'N/A'}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title="View Activity">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleViewUserActivity(user._id)}
                                disabled={processing}
                              >
                                <HistoryIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Block User">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => handleBlockUserClick(user._id, user.email, user.username)}
                                disabled={processing}
                              >
                                <BlockIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete User">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteUserClick(user._id, user.email)}
                                disabled={processing}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Paper sx={{ mt: 2, borderRadius: 3, boxShadow: 2 }}>
                <TablePagination
                  component="div"
                  count={usersTotal}
                  page={usersPage}
                  onPageChange={(e, newPage) => setUsersPage(newPage)}
                  rowsPerPage={usersRowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setUsersRowsPerPage(parseInt(e.target.value, 10));
                    setUsersPage(0);
                  }}
                  rowsPerPageOptions={[10, 20, 50, 100]}
                />
              </Paper>
            </>
          )}

          {/* Blocklist Section */}
          <Box sx={{ mt: 5 }}>
            <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight={700}>
                  Blocked Emails
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    placeholder="Search blocklist..."
                    value={blocklistSearch}
                    onChange={(e) => {
                      setBlocklistSearch(e.target.value);
                      setBlocklistPage(1);
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ minWidth: 250 }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={fetchBlocklist}
                    disabled={blocklistLoading}
                  >
                    Refresh
                  </Button>
                </Box>
              </Box>
            </Paper>

            {blocklistLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : blocklist.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, boxShadow: 2 }}>
                <BlockIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No blocked emails found
                </Typography>
              </Paper>
            ) : (
              <>
                <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Username</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Blocked By</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Blocked At</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {blocklist.map((block) => (
                        <TableRow key={block._id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {block.original_email || block.email}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {block.username || 'Unknown'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ maxWidth: 300 }}>
                              {block.reason || 'No reason provided'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {block.blocked_by?.email || 'Unknown'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {block.blockedAt 
                                ? (() => {
                                    let dateStr = block.blockedAt;
                                    if (dateStr && !dateStr.includes('Z') && !dateStr.includes('+') && !dateStr.includes('-', 10)) {
                                      dateStr = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
                                    }
                                    const date = new Date(dateStr);
                                    return new Intl.DateTimeFormat(undefined, {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      timeZoneName: 'short'
                                    }).format(date);
                                  })()
                                : 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Unblock Email">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleUnblockEmail(block._id, block.original_email || block.email)}
                              >
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={blocklistTotalPages}
                    page={blocklistPage}
                    onChange={(e, value) => {
                      setBlocklistPage(value);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    color="primary"
                    size="large"
                  />
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}

      {/* Settings Tab */}
      {currentTab === 2 && (
        <Box>
          <Paper sx={{ 
            p: 4,
            borderRadius: 3,
            boxShadow: 2,
          }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Admin Settings
            </Typography>
            <Divider sx={{ my: 3 }} />
            
            {/* Subscription Settings */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Subscription Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Enable or disable the yearly subscription option. Prices are managed in the Stripe dashboard.
              </Typography>
              
              {loadingSettings ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box sx={{ 
                    p: 3, 
                    bgcolor: 'grey.50', 
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'grey.200',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <FormControl>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <input
                            type="checkbox"
                            checked={subscriptionSettings.yearly_enabled}
                            onChange={(e) => setSubscriptionSettings({
                              ...subscriptionSettings,
                              yearly_enabled: e.target.checked
                            })}
                            style={{ width: 20, height: 20, cursor: 'pointer' }}
                          />
                          <Typography variant="body1" fontWeight={500}>
                            Enable Yearly Subscription Option
                          </Typography>
                        </Box>
                      </FormControl>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, ml: 4.5 }}>
                      When enabled, users will see both monthly and yearly subscription options. 
                      When disabled, only the monthly option will be available.
                    </Typography>
                  </Box>
                  
                  <Button
                    variant="contained"
                    onClick={handleSaveSubscriptionSettings}
                    disabled={savingSettings}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      alignSelf: 'flex-start',
                    }}
                  >
                    {savingSettings ? 'Saving...' : 'Save Subscription Settings'}
                  </Button>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Card-free promo access */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Free-Access Promo Codes
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create codes promoters can share in person. Redeeming a code unlocks VIP without a card, expires automatically, and can only be done once per account.
              </Typography>
              <Alert severity="info" sx={{ mb: 2.5, py: 0.5 }}>
                <strong>Best practice:</strong> use one code per event, set a realistic redemption cap and deadline, and deactivate it if it is shared publicly.
              </Alert>
              <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 3, bgcolor: alpha(theme.palette.primary.main, 0.018) }}>
                <Typography variant="subtitle1" fontWeight={800}>Create a promo code</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Set who the code is for, how much access it provides, and when new redemptions close.
                </Typography>

                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                  columnGap: 3,
                  rowGap: 2.5,
                }}>
                  <Box>
                    <Typography component="label" htmlFor="promo-code" variant="body2" fontWeight={700} sx={{ display: 'block', mb: 0.75 }}>
                      Promo code <Box component="span" color="text.secondary" fontWeight={400}>(optional)</Box>
                    </Typography>
                    <TextField id="promo-code" fullWidth size="small" placeholder="Example: MIAMIEXPO" value={promoForm.code}
                      helperText="Leave empty and OnlyStats will generate one."
                      onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })} inputProps={{ maxLength: 32 }} />
                  </Box>

                  <Box>
                    <Typography component="label" htmlFor="promo-campaign" variant="body2" fontWeight={700} sx={{ display: 'block', mb: 0.75 }}>
                      Campaign or promoter
                    </Typography>
                    <TextField id="promo-campaign" fullWidth size="small" placeholder="Example: Miami Expo" value={promoForm.label}
                      helperText="Only admins see this label."
                      onChange={(e) => setPromoForm({ ...promoForm, label: e.target.value })} />
                  </Box>

                  <Box>
                    <Typography component="label" htmlFor="promo-access-days" variant="body2" fontWeight={700} sx={{ display: 'block', mb: 0.75 }}>
                      Days of access
                    </Typography>
                    <TextField id="promo-access-days" fullWidth size="small" type="number" value={promoForm.durationDays}
                      helperText="VIP access per person (1–30 days)."
                      sx={{ '& input[type=number]': { MozAppearance: 'textfield' }, '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 } }}
                      onChange={(e) => setPromoForm({ ...promoForm, durationDays: e.target.value })} inputProps={{ min: 1, max: 30 }} />
                  </Box>

                  <Box>
                    <Typography component="label" htmlFor="promo-max-redemptions" variant="body2" fontWeight={700} sx={{ display: 'block', mb: 0.75 }}>
                      Maximum redemptions <Box component="span" color="text.secondary" fontWeight={400}>(optional)</Box>
                    </Typography>
                    <TextField id="promo-max-redemptions" fullWidth size="small" type="number" placeholder="Unlimited" value={promoForm.maxRedemptions}
                      helperText="Total accounts allowed to use this code."
                      sx={{ '& input[type=number]': { MozAppearance: 'textfield' }, '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 } }}
                      onChange={(e) => setPromoForm({ ...promoForm, maxRedemptions: e.target.value })} inputProps={{ min: 1 }} />
                  </Box>

                  <Box sx={{ gridColumn: { md: '1 / -1' }, pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" fontWeight={700} sx={{ mt: 2 }}>
                      Redemption deadline <Box component="span" color="error.main">*</Box>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      New redemptions close at this time; existing previews keep their full access period.
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 180px' }, gap: 1.5, mt: 1.25 }}>
                      <TextField id="promo-expiration-date" fullWidth size="small" type="date" value={promoForm.expiresAt}
                        aria-label="Redemption deadline date" required
                        inputProps={{ min: new Date().toISOString().slice(0, 10) }}
                        onChange={(e) => setPromoForm({ ...promoForm, expiresAt: e.target.value })} />
                      <FormControl fullWidth size="small">
                        <Select value={promoForm.expiresAtTime} aria-label="Redemption deadline time"
                          onChange={(e) => setPromoForm({ ...promoForm, expiresAtTime: e.target.value })}>
                          {PROMO_EXPIRATION_TIMES.map(([value, label]) => (
                            <MenuItem key={value} value={value}>{label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>

                  <Box sx={{ gridColumn: { md: '1 / -1' }, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={handleCreatePromo}
                      disabled={creatingPromo || !promoForm.expiresAt}
                      sx={{ minWidth: { xs: '100%', sm: 220 }, py: 1.25, textTransform: 'none', fontWeight: 700 }}>
                      {creatingPromo ? 'Creating...' : 'Create promo code'}
                    </Button>
                  </Box>
                </Box>
              </Paper>
              {promoCodes.length === 0 ? (
                <Alert severity="info">No promo codes have been created yet.</Alert>
              ) : (
                <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead><TableRow>
                      <TableCell>Code</TableCell><TableCell>Campaign</TableCell><TableCell>Access</TableCell>
                      <TableCell>Uses</TableCell><TableCell>Conversions</TableCell><TableCell>Redeem by</TableCell><TableCell>Status</TableCell><TableCell align="right">Action</TableCell>
                    </TableRow></TableHead>
                    <TableBody>{promoCodes.map((promo) => (
                      <TableRow key={promo._id}>
                        <TableCell><Typography fontFamily="monospace" fontWeight={700}>{promo.code}</Typography></TableCell>
                        <TableCell>{promo.label || '—'}</TableCell>
                        <TableCell>{promo.durationDays} days</TableCell>
                        <TableCell>{promo.redemptionCount}{promo.maxRedemptions ? ` / ${promo.maxRedemptions}` : ''}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700}>{promo.conversions || 0} paid</Typography>
                          <Typography variant="caption" color="text.secondary">{promo.conversionRate || 0}%</Typography>
                        </TableCell>
                        <TableCell>{promo.expiresAt ? new Date(promo.expiresAt).toLocaleString() : 'No expiry'}</TableCell>
                        <TableCell><Chip size="small"
                          color={isPromoExpired(promo) ? 'warning' : (promo.active ? 'success' : 'default')}
                          label={isPromoExpired(promo) ? 'Expired' : (promo.active ? 'Active' : 'Inactive')} /></TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Button size="small" startIcon={<VisibilityIcon />} onClick={() => handleViewPromoAnalytics(promo)}>
                              View users
                            </Button>
                            <Button size="small" color={promo.active ? 'error' : 'success'}
                              disabled={isPromoExpired(promo)} onClick={() => handleTogglePromo(promo)}>
                              {isPromoExpired(promo) ? 'Expired' : (promo.active ? 'Deactivate' : 'Activate')}
                            </Button>
                            <Tooltip title={promo.redemptionCount > 0 ? 'Deactivate codes with redemptions to preserve history' : 'Permanently delete this unused code'}>
                              <span>
                                <Button size="small" color="error" startIcon={<DeleteIcon />}
                                  disabled={promo.redemptionCount > 0} onClick={() => setPromoToDelete(promo)}>
                                  Delete
                                </Button>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}</TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            <Dialog open={Boolean(promoToDelete)} onClose={() => !deletingPromo && setPromoToDelete(null)}>
              <DialogTitle>Delete promo code?</DialogTitle>
              <DialogContent>
                <Typography>
                  This will permanently delete the unused code <strong>{promoToDelete?.code}</strong>. This cannot be undone.
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setPromoToDelete(null)} disabled={deletingPromo}>Cancel</Button>
                <Button color="error" variant="contained" onClick={handleDeletePromo} disabled={deletingPromo}>
                  {deletingPromo ? 'Deleting...' : 'Delete code'}
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog open={promoAnalyticsOpen} onClose={() => setPromoAnalyticsOpen(false)} fullWidth maxWidth="md">
              <DialogTitle>
                {promoAnalytics?.promoCode?.code || 'Promo code'} results
              </DialogTitle>
              <DialogContent dividers>
                {promoAnalyticsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
                ) : (
                  <>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      {[
                        ['Redeemed', promoAnalytics?.summary?.totalRedemptions || 0, 'primary'],
                        ['Free now', promoAnalytics?.summary?.activePreviews || 0, 'info'],
                        ['Converted to paid', promoAnalytics?.summary?.conversions || 0, 'success'],
                        ['Expired unpaid', promoAnalytics?.summary?.expiredWithoutPayment || 0, 'warning'],
                      ].map(([label, value, color]) => (
                        <Grid item xs={6} md={3} key={label}>
                          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                            <Typography variant="h5" fontWeight={800} color={`${color}.main`}>{value}</Typography>
                            <Typography variant="body2" color="text.secondary">{label}</Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                    <Alert severity="success" sx={{ mb: 2 }}>
                      Conversion rate: <strong>{promoAnalytics?.summary?.conversionRate || 0}%</strong> of redeemers became paying subscribers.
                    </Alert>
                    {(promoAnalytics?.accounts || []).length === 0 ? (
                      <Alert severity="info">Nobody has redeemed this code yet.</Alert>
                    ) : (
                      <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                        <Table size="small">
                          <TableHead><TableRow>
                            <TableCell>Account</TableCell><TableCell>Redeemed</TableCell><TableCell>Free access ends</TableCell><TableCell>Outcome</TableCell>
                          </TableRow></TableHead>
                          <TableBody>{promoAnalytics.accounts.map((account) => (
                            <TableRow key={account._id}>
                              <TableCell>
                                <Typography variant="body2" fontWeight={700}>{account.email}</Typography>
                                <Typography variant="caption" color="text.secondary">{account.username}</Typography>
                              </TableCell>
                              <TableCell>{account.redeemedAt ? new Date(account.redeemedAt).toLocaleString() : '—'}</TableCell>
                              <TableCell>{account.accessEndsAt ? new Date(account.accessEndsAt).toLocaleString() : '—'}</TableCell>
                              <TableCell>
                                <Chip size="small"
                                  label={account.previewStatus === 'converted' ? 'Converted to paid' : account.previewStatus === 'active' ? 'Free' : 'Expired unpaid'}
                                  color={account.previewStatus === 'converted' ? 'success' : account.previewStatus === 'active' ? 'info' : 'default'} />
                                {account.convertedAt && (
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                    {new Date(account.convertedAt).toLocaleString()}
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}</TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </>
                )}
              </DialogContent>
              <DialogActions><Button onClick={() => setPromoAnalyticsOpen(false)}>Close</Button></DialogActions>
            </Dialog>

            <Divider sx={{ my: 4 }} />

            {/* Admin Invitation */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Invite Admin User
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Invite a user to become an admin. The user must already have an OnlyStats account.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Email Address"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="user@example.com"
                  sx={{ 
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={handleInviteAdmin}
                  disabled={inviting || !inviteEmail}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                  }}
                >
                  {inviting ? 'Inviting...' : 'Invite Admin'}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Support Tickets Tab */}
      {currentTab === 3 && (
        <Box>
          <Paper sx={{ 
            p: 3, 
            mb: 3,
            borderRadius: 3,
            boxShadow: 2,
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" fontWeight={700}>
                Support Tickets
              </Typography>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={ticketsStatusFilter}
                  label="Status Filter"
                  onChange={(e) => {
                    setTicketsStatusFilter(e.target.value);
                    setTicketsPage(1);
                  }}
                  sx={{
                    borderRadius: 2,
                  }}
                >
                  <MenuItem value="active">Active (Pending + Followed Up)</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="followed_up">Followed Up</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                  <MenuItem value="dismissed">Dismissed</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Paper>

          {ticketsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : supportTickets.length === 0 ? (
            <Paper sx={{ 
              p: 4, 
              textAlign: 'center',
              borderRadius: 3,
              boxShadow: 2,
            }}>
              <SupportIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No support tickets found
              </Typography>
            </Paper>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {supportTickets.map((ticket) => (
                      <TableRow key={ticket._id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {ticket.user?.username || 'Unknown'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {ticket.user?.email || 'Unknown'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 300 }}>
                            {ticket.subject}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={capitalizeFirst(ticket.category || 'other')}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={capitalizeFirst(ticket.status === 'followed_up' ? 'Followed Up' : ticket.status)}
                            color={
                              ticket.status === 'resolved' ? 'success' :
                              ticket.status === 'followed_up' ? 'info' :
                              ticket.status === 'dismissed' ? 'default' :
                              'warning'
                            }
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {ticket.createdAt 
                              ? new Date(ticket.createdAt).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                })
                              : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedTicket(ticket);
                                  setTicketStatusNote(ticket.resolution_note || '');
                                  setTicketAdminNotes(ticket.admin_notes || '');
                                  setTicketStatusDialogOpen(true);
                                }}
                                sx={{
                                  color: 'primary.main',
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {ticket.status === 'pending' && (
                              <>
                                <Tooltip title="Mark as Followed Up">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleUpdateTicketStatus(ticket, 'followed_up')}
                                    sx={{
                                      color: 'info.main',
                                    }}
                                  >
                                    <CheckCircleIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Resolve">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleUpdateTicketStatus(ticket, 'resolved')}
                                    sx={{
                                      color: 'success.main',
                                    }}
                                  >
                                    <CheckCircleIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Dismiss">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleUpdateTicketStatus(ticket, 'dismissed')}
                                    sx={{
                                      color: 'error.main',
                                    }}
                                  >
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            {ticket.status === 'followed_up' && (
                              <>
                                <Tooltip title="Resolve">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleUpdateTicketStatus(ticket, 'resolved')}
                                    sx={{
                                      color: 'success.main',
                                    }}
                                  >
                                    <CheckCircleIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Dismiss">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleUpdateTicketStatus(ticket, 'dismissed')}
                                    sx={{
                                      color: 'error.main',
                                    }}
                                  >
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={ticketsTotalPages}
                  page={ticketsPage}
                  onChange={(e, value) => {
                    setTicketsPage(value);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  color="primary"
                  size="large"
                />
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Developer Tab - Security Logs */}
      {currentTab === 4 && (
        <Box>
          {/* Security Stats Summary */}
          {securityStats && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Last 24 Hours
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
                      {securityStats.last_24h?.total || 0}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      {securityStats.last_24h?.critical > 0 && (
                        <Chip label={`${securityStats.last_24h.critical} Critical`} size="small" color="error" />
                      )}
                      {securityStats.last_24h?.high > 0 && (
                        <Chip label={`${securityStats.last_24h.high} High`} size="small" color="warning" />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Last 7 Days
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
                      {securityStats.last_7d?.total || 0}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      {securityStats.last_7d?.critical > 0 && (
                        <Chip label={`${securityStats.last_7d.critical} Critical`} size="small" color="error" />
                      )}
                      {securityStats.last_7d?.high > 0 && (
                        <Chip label={`${securityStats.last_7d.high} High`} size="small" color="warning" />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Last 30 Days
                    </Typography>
                    <Typography variant="h4" fontWeight={700}>
                      {securityStats.last_30d?.total || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Top Activity Types
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {securityStats.top_activity_types?.slice(0, 3).map((activity, idx) => (
                        <Typography key={idx} variant="body2" fontWeight={500}>
                          {activity.type}: {activity.count}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Filters */}
          <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Severity</InputLabel>
                <Select
                  value={securityLogsSeverityFilter}
                  label="Severity"
                  onChange={(e) => {
                    setSecurityLogsSeverityFilter(e.target.value);
                    setSecurityLogsPage(1);
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Activity Type</InputLabel>
                <Select
                  value={securityLogsActivityFilter}
                  label="Activity Type"
                  onChange={(e) => {
                    setSecurityLogsActivityFilter(e.target.value);
                    setSecurityLogsPage(1);
                  }}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="rate_limit_exceeded">Rate Limit Exceeded</MenuItem>
                  <MenuItem value="unverified_review_rate_limit_exceeded">Unverified Review Limit</MenuItem>
                  <MenuItem value="suspicious_review_pattern">Suspicious Review Pattern</MenuItem>
                  <MenuItem value="multiple_failed_logins">Multiple Failed Logins</MenuItem>
                  <MenuItem value="unusual_api_usage">Unusual API Usage</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Time Range</InputLabel>
                <Select
                  value={securityLogsDays}
                  label="Time Range"
                  onChange={(e) => {
                    setSecurityLogsDays(e.target.value);
                    setSecurityLogsPage(1);
                  }}
                >
                  <MenuItem value={1}>Last 24h</MenuItem>
                  <MenuItem value={7}>Last 7 days</MenuItem>
                  <MenuItem value={30}>Last 30 days</MenuItem>
                  <MenuItem value={90}>Last 90 days</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchSecurityLogs}
                disabled={securityLogsLoading}
              >
                Refresh
              </Button>
            </Box>
          </Paper>

          {/* Security Logs Table */}
          {securityLogsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : securityLogs.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, boxShadow: 2 }}>
              <SecurityIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No security logs found
              </Typography>
            </Paper>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Timestamp</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Activity Type</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Severity</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Details</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>IP Address</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {securityLogs.map((log) => {
                      const hasReviews = log.reviews && log.reviews.length > 0;
                      const isReviewRelated = log.activity_type?.includes('review');
                      const isClickable = hasReviews || isReviewRelated;
                      return (
                        <TableRow 
                          key={log._id} 
                          hover
                          onClick={() => {
                            if (isClickable) {
                              setSelectedSecurityLogReviews(log);
                              setSecurityLogReviewsDialogOpen(true);
                            }
                          }}
                          sx={{
                            cursor: isClickable ? 'pointer' : 'default',
                            '&:hover': isClickable ? {
                              bgcolor: alpha(theme.palette.primary.main, 0.04),
                            } : {},
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2">
                              {log.createdAt 
                                ? (() => {
                                    // Parse ISO string - if it doesn't have timezone, treat as UTC
                                    let dateStr = log.createdAt;
                                    if (dateStr && !dateStr.includes('Z') && !dateStr.includes('+') && !dateStr.includes('-', 10)) {
                                      // No timezone info, assume UTC
                                      dateStr = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
                                    }
                                    const date = new Date(dateStr);
                                    // Format in local timezone
                                    return new Intl.DateTimeFormat(undefined, {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      timeZoneName: 'short'
                                    }).format(date);
                                  })()
                                : 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {log.user?.username || 'Unknown'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {log.user?.email || 'Unknown'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={log.activity_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown'}
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={log.severity?.toUpperCase() || 'MEDIUM'}
                              size="small"
                              color={
                                log.severity === 'critical' ? 'error' :
                                log.severity === 'high' ? 'warning' :
                                log.severity === 'low' ? 'default' :
                                'info'
                              }
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ maxWidth: 400 }}>
                                {log.details || 'No details'}
                              </Typography>
                              {hasReviews ? (
                                <Chip 
                                  label={`${log.reviews.length} Review${log.reviews.length > 1 ? 's' : ''}`}
                                  size="small"
                                  color="primary"
                                  sx={{ fontWeight: 600, cursor: 'pointer' }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedSecurityLogReviews(log);
                                    setSecurityLogReviewsDialogOpen(true);
                                  }}
                                />
                              ) : isReviewRelated && (
                                <Chip 
                                  label="View Reviews"
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  sx={{ fontWeight: 600, cursor: 'pointer' }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedSecurityLogReviews(log);
                                    setSecurityLogReviewsDialogOpen(true);
                                  }}
                                />
                              )}
                            </Box>
                            {log.metadata && Object.keys(log.metadata).length > 0 && (
                              <Tooltip title={JSON.stringify(log.metadata, null, 2)}>
                                <Chip label="View Metadata" size="small" sx={{ mt: 0.5 }} />
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary" fontFamily="monospace">
                              {log.ip_address || 'N/A'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={securityLogsTotalPages}
                  page={securityLogsPage}
                  onChange={(e, value) => {
                    setSecurityLogsPage(value);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  color="primary"
                  size="large"
                />
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Ticket Status Update Dialog */}
      <Dialog
        open={ticketStatusDialogOpen}
        onClose={() => !updatingTicketStatus && setTicketStatusDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SupportIcon color="primary" />
            <Typography variant="h6">
              {selectedTicket ? `Update Ticket: ${selectedTicket.subject}` : 'Update Ticket Status'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedTicket && (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  User
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {selectedTicket.user?.username || 'Unknown'} ({selectedTicket.user?.email || 'Unknown'})
                </Typography>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Message
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {selectedTicket.message}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={selectedTicket.pendingStatus || selectedTicket.status}
                    label="Status"
                    onChange={(e) => setSelectedTicket({ ...selectedTicket, pendingStatus: e.target.value })}
                    disabled={updatingTicketStatus}
                  >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="followed_up">Followed Up</MenuItem>
                    <MenuItem value="resolved">Resolved</MenuItem>
                    <MenuItem value="dismissed">Dismissed</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Resolution Note (visible to user)"
                placeholder="This note will be sent to the user..."
                value={ticketStatusNote}
                onChange={(e) => setTicketStatusNote(e.target.value)}
                disabled={updatingTicketStatus}
                sx={{ mb: 3 }}
              />
              
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Admin Notes (internal only)"
                placeholder="Internal notes for other admins..."
                value={ticketAdminNotes}
                onChange={(e) => setTicketAdminNotes(e.target.value)}
                disabled={updatingTicketStatus}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setTicketStatusDialogOpen(false);
              setSelectedTicket(null);
              setTicketStatusNote('');
              setTicketAdminNotes('');
            }}
            disabled={updatingTicketStatus}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmTicketStatusUpdate}
            variant="contained"
            disabled={updatingTicketStatus}
            startIcon={updatingTicketStatus ? <CircularProgress size={16} /> : <CheckCircleIcon />}
          >
            {updatingTicketStatus ? 'Updating...' : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Review Confirmation Dialog */}
      <Dialog
        open={deleteReviewDialogOpen}
        onClose={() => !processing && setDeleteReviewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon color="error" />
            <Typography variant="h6">Delete Review</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              This action cannot be undone.
            </Typography>
            <Typography variant="body2">
              The review will be permanently deleted from the database.
            </Typography>
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            To confirm deletion, please type <strong>"delete"</strong> in the field below:
          </Typography>
          <TextField
            fullWidth
            label="Type 'delete' to confirm"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            disabled={processing}
            error={deleteConfirmText && deleteConfirmText.toLowerCase() !== 'delete'}
            helperText={
              deleteConfirmText && deleteConfirmText.toLowerCase() !== 'delete'
                ? 'Please type "delete" exactly'
                : ''
            }
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setDeleteReviewDialogOpen(false);
              setReviewToDelete(null);
              setDeleteConfirmText('');
            }}
            disabled={processing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteReviewConfirm}
            variant="contained"
            color="error"
            disabled={processing || deleteConfirmText.toLowerCase() !== 'delete'}
            startIcon={processing ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {processing ? 'Deleting...' : 'Delete Review'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog
        open={deleteUserDialogOpen}
        onClose={() => !processing && setDeleteUserDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon color="error" />
            <Typography variant="h6">Delete User Account</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              This action cannot be undone.
            </Typography>
            <Typography variant="body2">
              The user account and all associated data (reviews, reports) will be permanently deleted.
            </Typography>
          </Alert>
          {userToDelete && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              User: <strong>{userToDelete.email}</strong>
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            To confirm deletion, please type <strong>"delete"</strong> in the field below:
          </Typography>
          <TextField
            fullWidth
            label="Type 'delete' to confirm"
            value={deleteUserConfirmText}
            onChange={(e) => setDeleteUserConfirmText(e.target.value)}
            disabled={processing}
            error={deleteUserConfirmText && deleteUserConfirmText.toLowerCase() !== 'delete'}
            helperText={
              deleteUserConfirmText && deleteUserConfirmText.toLowerCase() !== 'delete'
                ? 'Please type "delete" exactly'
                : ''
            }
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setDeleteUserDialogOpen(false);
              setUserToDelete(null);
              setDeleteUserConfirmText('');
            }}
            disabled={processing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteUserConfirm}
            variant="contained"
            color="error"
            disabled={processing || deleteUserConfirmText.toLowerCase() !== 'delete'}
            startIcon={processing ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {processing ? 'Deleting...' : 'Delete User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Subscription Settings Confirmation Dialog */}
      <Dialog
        open={confirmSettingsDialogOpen}
        onClose={() => !savingSettings && setConfirmSettingsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon sx={{ color: 'warning.main', fontSize: 28 }} />
            <Typography variant="h6">Confirm Subscription Settings Update</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              This will affect production settings
            </Typography>
            <Typography variant="body2">
              These changes will be applied immediately and will affect all new subscription signups.
            </Typography>
          </Alert>

          {pendingSettings && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                New Settings:
              </Typography>
              <Box sx={{ 
                bgcolor: 'grey.50', 
                p: 2, 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
              }}>
                <Typography variant="body2">
                  <strong>Yearly Option:</strong> {pendingSettings.yearly_enabled ? 'Enabled' : 'Disabled'}
                </Typography>
              </Box>
            </Box>
          )}

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Important Notice
            </Typography>
            <Typography variant="body2">
              This will enable or disable the yearly subscription option for new signups. 
              Subscription prices are managed directly in the Stripe dashboard.
            </Typography>
          </Alert>

          <Typography variant="body2" color="text.secondary">
            Are you sure you want to update these subscription settings?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setConfirmSettingsDialogOpen(false);
              setPendingSettings(null);
            }}
            disabled={savingSettings}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSaveSettings}
            variant="contained"
            color="primary"
            disabled={savingSettings}
            startIcon={savingSettings ? <CircularProgress size={16} /> : <SettingsIcon />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {savingSettings ? 'Updating...' : 'Confirm & Update Settings'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Block User Confirmation Dialog */}
      <Dialog
        open={blockUserDialogOpen}
        onClose={() => !processing && setBlockUserDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BlockIcon color="warning" />
            <Typography variant="h6">Block User</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              This will block the user and delete their account.
            </Typography>
            <Typography variant="body2">
              The user's email will be added to the blocklist, preventing them from signing up again. Their account and all associated data will be permanently deleted.
            </Typography>
          </Alert>
          {userToBlock && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                User: <strong>{userToBlock.email}</strong>
              </Typography>
              {userToBlock.username && (
                <Typography variant="body2" color="text.secondary">
                  Username: <strong>{userToBlock.username}</strong>
                </Typography>
              )}
            </Box>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            To confirm blocking, please enter the user's email address:
          </Typography>
          <TextField
            fullWidth
            label="Enter user email to confirm"
            value={blockUserConfirmEmail}
            onChange={(e) => setBlockUserConfirmEmail(e.target.value)}
            disabled={processing}
            error={blockUserConfirmEmail && blockUserConfirmEmail.toLowerCase() !== userToBlock?.email?.toLowerCase()}
            helperText={
              blockUserConfirmEmail && blockUserConfirmEmail.toLowerCase() !== userToBlock?.email?.toLowerCase()
                ? 'Email does not match'
                : ''
            }
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Reason for blocking (optional)"
            placeholder="e.g., Violation of terms, spam, etc."
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
            disabled={processing}
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setBlockUserDialogOpen(false);
              setUserToBlock(null);
              setBlockReason('');
              setBlockUserConfirmEmail('');
            }}
            disabled={processing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBlockUserConfirm}
            variant="contained"
            color="warning"
            disabled={processing || !userToBlock || blockUserConfirmEmail.toLowerCase() !== userToBlock.email.toLowerCase()}
            startIcon={processing ? <CircularProgress size={16} /> : <BlockIcon />}
          >
            {processing ? 'Blocking...' : 'Block User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Activity Dialog */}
      <Dialog
        open={userActivityDialogOpen}
        onClose={() => setUserActivityDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            maxHeight: '90vh',
            overflow: 'hidden',
            animation: 'fadeInScale 0.3s ease-out',
            '@keyframes fadeInScale': {
              '0%': {
                opacity: 0,
                transform: 'scale(0.95)',
              },
              '100%': {
                opacity: 1,
                transform: 'scale(1)',
              },
            },
          },
        }}
        TransitionProps={{
          timeout: 300,
        }}
      >
        <DialogTitle sx={{ 
          pb: 2.5, 
          pt: 3.5,
          px: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.04)} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              p: 1.5,
              borderRadius: 2.5,
              bgcolor: 'primary.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
              },
            }}>
              <HistoryIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
                User Activity
              </Typography>
              {selectedUserActivity && (
                <Typography variant="body2" color="text.secondary">
                  {selectedUserActivity.user.email}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, bgcolor: 'grey.50' }}>
          {loadingActivity ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
              <CircularProgress />
            </Box>
          ) : selectedUserActivity ? (
            <Box sx={{ p: { xs: 2.5, sm: 3, md: 3 } }}>
              {/* Desktop Layout */}
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                {/* User Info Card */}
                <Card sx={{ 
                  mb: 3,
                  borderRadius: 3,
                  boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.08)}`,
                  background: 'white',
                  border: 'none',
                  overflow: 'hidden',
                  position: 'relative',
                  animation: 'slideInUp 0.4s ease-out 0.1s both',
                }}>
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  }} />
                  <CardContent sx={{ p: 3.5 }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" color="text.secondary" sx={{ 
                          textTransform: 'uppercase', 
                          letterSpacing: 1.2, 
                          fontSize: '0.7rem', 
                          fontWeight: 600,
                          display: 'block',
                          mb: 1,
                        }}>
                          Email
                        </Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ color: 'text.primary' }}>
                          {selectedUserActivity.user.email}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" color="text.secondary" sx={{ 
                          textTransform: 'uppercase', 
                          letterSpacing: 1.2, 
                          fontSize: '0.7rem', 
                          fontWeight: 600,
                          display: 'block',
                          mb: 1,
                        }}>
                          Username
                        </Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ color: 'text.primary' }}>
                          {selectedUserActivity.user.username}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" color="text.secondary" sx={{ 
                          textTransform: 'uppercase', 
                          letterSpacing: 1.2, 
                          fontSize: '0.7rem', 
                          fontWeight: 600,
                          display: 'block',
                          mb: 1,
                        }}>
                          Joined
                        </Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ color: 'text.primary' }}>
                          {selectedUserActivity.user.joinedAt || selectedUserActivity.user.createdAt
                            ? new Date(selectedUserActivity.user.joinedAt || selectedUserActivity.user.createdAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })
                            : 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" color="text.secondary" sx={{ 
                          textTransform: 'uppercase', 
                          letterSpacing: 1.2, 
                          fontSize: '0.7rem', 
                          fontWeight: 600,
                          display: 'block',
                          mb: 1,
                        }}>
                          Subscription
                        </Typography>
                        <Chip 
                          label={selectedUserActivity.subscription.status === 'active' 
                            ? `Active (${selectedUserActivity.subscription.interval === 'year' ? 'Yearly' : selectedUserActivity.subscription.interval === 'month' ? 'Monthly' : selectedUserActivity.subscription.plan === 'yearly' ? 'Yearly' : selectedUserActivity.subscription.plan === 'monthly' ? 'Monthly' : 'Active'})`
                            : 'Inactive'}
                          size="small"
                          sx={{ 
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            bgcolor: selectedUserActivity.subscription.status === 'active' 
                              ? alpha(theme.palette.info.main, 0.1)
                              : alpha(theme.palette.grey[400], 0.1),
                            color: selectedUserActivity.subscription.status === 'active' 
                              ? 'info.main'
                              : 'grey.700',
                            border: `1px solid ${selectedUserActivity.subscription.status === 'active' 
                              ? alpha(theme.palette.info.main, 0.3)
                              : alpha(theme.palette.grey[400], 0.3)}`,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" color="text.secondary" sx={{ 
                          textTransform: 'uppercase', 
                          letterSpacing: 1.2, 
                          fontSize: '0.7rem', 
                          fontWeight: 600,
                          display: 'block',
                          mb: 1,
                        }}>
                          Verification
                        </Typography>
                        <Chip 
                          label={capitalizeFirst(selectedUserActivity.verification.verificationStatus || 'unverified')}
                          size="small"
                          sx={{ 
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            bgcolor: selectedUserActivity.verification.verified 
                              ? alpha(theme.palette.primary.main, 0.1)
                              : alpha(theme.palette.grey[400], 0.1),
                            color: selectedUserActivity.verification.verified 
                              ? 'primary.main'
                              : 'grey.700',
                            border: `1px solid ${selectedUserActivity.verification.verified 
                              ? alpha(theme.palette.primary.main, 0.3)
                              : alpha(theme.palette.grey[400], 0.3)}`,
                          }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Verification Details Section - Desktop Only (only show if verifiedAt or lastScrapeAt exist) */}
                {(selectedUserActivity.verification.verifiedAt || selectedUserActivity.verification.lastScrapeAt) && (
                  <Card sx={{ 
                    mb: 3,
                    borderRadius: 3,
                    boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.08)}`,
                    background: 'white',
                    border: 'none',
                    overflow: 'hidden',
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    animation: 'slideInUp 0.4s ease-out 0.2s both',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.12)}`,
                    },
                  }}>
                    <Box sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      background: selectedUserActivity.verification.verified 
                        ? theme.palette.primary.main
                        : theme.palette.grey[400],
                    }} />
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                        <Box sx={{
                          p: 1,
                          borderRadius: 1.5,
                          bgcolor: selectedUserActivity.verification.verified 
                            ? alpha(theme.palette.primary.main, 0.1)
                            : alpha(theme.palette.grey[400], 0.1),
                          color: selectedUserActivity.verification.verified 
                            ? 'primary.main'
                            : 'grey.600',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          {selectedUserActivity.verification.verified ? (
                            <VerifiedUserIcon sx={{ fontSize: 24 }} />
                          ) : (
                            <CancelIcon sx={{ fontSize: 24 }} />
                          )}
                        </Box>
                        <Typography variant="h6" fontWeight={700}>
                          Verification Details
                        </Typography>
                      </Box>
                      
                      <Grid container spacing={3}>
                        {selectedUserActivity.verification.verifiedAt && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ 
                              textTransform: 'uppercase', 
                              letterSpacing: 1, 
                              fontSize: '0.7rem', 
                              fontWeight: 600, 
                              display: 'block', 
                              mb: 0.5,
                            }}>
                              Verified At
                            </Typography>
                            <Typography variant="body2" fontWeight={500} sx={{ color: 'text.primary' }}>
                              {new Date(selectedUserActivity.verification.verifiedAt).toLocaleString()}
                            </Typography>
                          </Grid>
                        )}
                        
                        {selectedUserActivity.verification.lastScrapeAt && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ 
                              textTransform: 'uppercase', 
                              letterSpacing: 1, 
                              fontSize: '0.7rem', 
                              fontWeight: 600, 
                              display: 'block', 
                              mb: 0.5,
                            }}>
                              Last Scrape
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="body2" fontWeight={500} sx={{ color: 'text.primary' }}>
                                {new Date(selectedUserActivity.verification.lastScrapeAt).toLocaleString()}
                              </Typography>
                              {selectedUserActivity.verification.lastScrapeStatus && (
                                <Chip 
                                  label={capitalizeFirst(selectedUserActivity.verification.lastScrapeStatus)} 
                                  size="small" 
                                  sx={{ 
                                    height: 22, 
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    bgcolor: selectedUserActivity.verification.lastScrapeStatus === 'success' 
                                      ? alpha(theme.palette.primary.main, 0.1)
                                      : alpha(theme.palette.grey[400], 0.1),
                                    color: selectedUserActivity.verification.lastScrapeStatus === 'success' 
                                      ? 'primary.main'
                                      : 'grey.700',
                                  }}
                                />
                              )}
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                      
                      {selectedUserActivity.verification.verificationError && (
                        <Alert 
                          severity="error" 
                          sx={{ 
                            mt: 3,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.error.main, 0.08),
                            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                          }}
                        >
                          <Typography variant="body2">
                            <strong>Error:</strong> {selectedUserActivity.verification.verificationError}
                          </Typography>
                        </Alert>
                      )}
                      
                      {selectedUserActivity.verification.mfaRequired && (
                        <Chip 
                          label="MFA Required" 
                          size="small" 
                          sx={{ 
                            mt: 2,
                            fontWeight: 600,
                            bgcolor: alpha(theme.palette.warning.main, 0.1),
                            color: 'warning.dark',
                            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                          }}
                        />
                      )}
                    </CardContent>
                  </Card>
                )}
              </Box>

              {/* Separate Cards for Mobile Only */}
              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <Grid container spacing={2.5}>
                  {/* User Info Card - Mobile */}
                  <Grid item xs={12}>
                    <Card sx={{ 
                      borderRadius: 3,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
                      background: 'white',
                      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      animation: 'slideInUp 0.4s ease-out 0.1s both',
                      mb: 2.5,
                    }}>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                          <Box sx={{
                            p: 1.2,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}>
                            <AccountCircleIcon sx={{ fontSize: 26 }} />
                          </Box>
                          <Typography variant="h6" fontWeight={700}>
                            User Information
                          </Typography>
                        </Box>
                        <Grid container spacing={2.5}>
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1.2, fontSize: '0.7rem', fontWeight: 600, display: 'block', mb: 0.75 }}>
                              Email
                            </Typography>
                            <Typography variant="body1" fontWeight={600} sx={{ color: 'text.primary' }}>
                              {selectedUserActivity.user.email}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1.2, fontSize: '0.7rem', fontWeight: 600, display: 'block', mb: 0.75 }}>
                              Username
                            </Typography>
                            <Typography variant="body1" fontWeight={600} sx={{ color: 'text.primary' }}>
                              {selectedUserActivity.user.username}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1.2, fontSize: '0.7rem', fontWeight: 600, display: 'block', mb: 0.75 }}>
                              Joined
                            </Typography>
                            <Typography variant="body1" fontWeight={600} sx={{ color: 'text.primary' }}>
                              {selectedUserActivity.user.joinedAt || selectedUserActivity.user.createdAt
                                ? new Date(selectedUserActivity.user.joinedAt || selectedUserActivity.user.createdAt).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })
                                : 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1.2, fontSize: '0.7rem', fontWeight: 600, display: 'block', mb: 0.75 }}>
                              Subscription
                            </Typography>
                            <Chip 
                              label={selectedUserActivity.subscription.status === 'active' 
                                ? `Active (${selectedUserActivity.subscription.interval === 'year' ? 'Yearly' : selectedUserActivity.subscription.interval === 'month' ? 'Monthly' : selectedUserActivity.subscription.plan === 'yearly' ? 'Yearly' : selectedUserActivity.subscription.plan === 'monthly' ? 'Monthly' : 'Active'})`
                                : 'Inactive'}
                              size="small"
                              sx={{ 
                                fontWeight: 600,
                                fontSize: '0.85rem',
                                bgcolor: selectedUserActivity.subscription.status === 'active' 
                                  ? alpha(theme.palette.info.main, 0.1)
                                  : alpha(theme.palette.grey[400], 0.1),
                                color: selectedUserActivity.subscription.status === 'active' 
                                  ? 'info.main'
                                  : 'grey.700',
                                border: `1px solid ${selectedUserActivity.subscription.status === 'active' 
                                  ? alpha(theme.palette.info.main, 0.3)
                                  : alpha(theme.palette.grey[400], 0.3)}`,
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1.2, fontSize: '0.7rem', fontWeight: 600, display: 'block', mb: 0.75 }}>
                              Verification
                            </Typography>
                            <Chip 
                              label={capitalizeFirst(selectedUserActivity.verification.verificationStatus || 'unverified')}
                              size="small"
                              sx={{ 
                                fontWeight: 600,
                                fontSize: '0.85rem',
                                bgcolor: selectedUserActivity.verification.verified 
                                  ? alpha(theme.palette.primary.main, 0.1)
                                  : alpha(theme.palette.grey[400], 0.1),
                                color: selectedUserActivity.verification.verified 
                                  ? 'primary.main'
                                  : 'grey.700',
                                border: `1px solid ${selectedUserActivity.verification.verified 
                                  ? alpha(theme.palette.primary.main, 0.3)
                                  : alpha(theme.palette.grey[400], 0.3)}`,
                              }}
                            />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Verification Details Card - Mobile Only (only show if verifiedAt or lastScrapeAt exist) */}
                  {(selectedUserActivity.verification.verifiedAt || selectedUserActivity.verification.lastScrapeAt) && (
                    <Grid item xs={12}>
                      <Card sx={{ 
                        borderRadius: 3,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
                        background: 'white',
                        border: `1px solid ${selectedUserActivity.verification.verified 
                          ? alpha(theme.palette.primary.main, 0.3) 
                          : alpha(theme.palette.grey[400], 0.5)}`,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        animation: 'slideInUp 0.4s ease-out 0.2s both',
                        mb: 2.5,
                      }}>
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                            <Box sx={{
                              p: 1.2,
                              borderRadius: 2,
                              bgcolor: selectedUserActivity.verification.verified 
                                ? alpha(theme.palette.primary.main, 0.15)
                                : alpha(theme.palette.grey[400], 0.15),
                              color: selectedUserActivity.verification.verified 
                                ? 'primary.main'
                                : 'grey.600',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}>
                              {selectedUserActivity.verification.verified ? (
                                <VerifiedUserIcon sx={{ fontSize: 26 }} />
                              ) : (
                                <CancelIcon sx={{ fontSize: 26 }} />
                              )}
                            </Box>
                            <Typography variant="h6" fontWeight={700}>
                              Verification Details
                            </Typography>
                          </Box>
                          
                          <Grid container spacing={3}>
                            {selectedUserActivity.verification.verifiedAt && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1.2, fontSize: '0.7rem', fontWeight: 600, display: 'block', mb: 0.75 }}>
                                  Verified At
                                </Typography>
                                <Typography variant="body2" fontWeight={500} sx={{ color: 'text.primary' }}>
                                  {new Date(selectedUserActivity.verification.verifiedAt).toLocaleString()}
                                </Typography>
                              </Grid>
                            )}
                            
                            {selectedUserActivity.verification.lastScrapeAt && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1.2, fontSize: '0.7rem', fontWeight: 600, display: 'block', mb: 0.75 }}>
                                  Last Scrape
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <Typography variant="body2" fontWeight={500} sx={{ color: 'text.primary' }}>
                                    {new Date(selectedUserActivity.verification.lastScrapeAt).toLocaleString()}
                                  </Typography>
                                  {selectedUserActivity.verification.lastScrapeStatus && (
                                    <Chip 
                                      label={capitalizeFirst(selectedUserActivity.verification.lastScrapeStatus)} 
                                      size="small" 
                                      sx={{ 
                                        height: 20, 
                                        fontSize: '0.65rem',
                                        bgcolor: selectedUserActivity.verification.lastScrapeStatus === 'success' 
                                          ? alpha(theme.palette.primary.main, 0.1)
                                          : alpha(theme.palette.grey[400], 0.1),
                                        color: selectedUserActivity.verification.lastScrapeStatus === 'success' 
                                          ? 'primary.main'
                                          : 'grey.700',
                                      }}
                                    />
                                  )}
                                </Box>
                              </Grid>
                            )}
                          </Grid>
                          
                          {selectedUserActivity.verification.verificationError && (
                            <Alert 
                              severity="error" 
                              sx={{ 
                                mt: 3,
                                bgcolor: alpha(theme.palette.error.main, 0.08),
                                border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                              }}
                            >
                              <Typography variant="body2">
                                <strong>Error:</strong> {selectedUserActivity.verification.verificationError}
                              </Typography>
                            </Alert>
                          )}
                          
                          {selectedUserActivity.verification.mfaRequired && (
                            <Chip 
                              label="MFA Required" 
                              size="small" 
                              sx={{ 
                                mt: 2,
                                bgcolor: alpha(theme.palette.warning.main, 0.1),
                                color: 'warning.dark',
                                border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                              }}
                            />
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                </Grid>
              </Box>

              {/* Reviews Section */}
              <Card sx={{ 
                borderRadius: 3,
                boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
                background: 'white',
                border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                animation: 'slideInUp 0.4s ease-out 0.4s both',
                '&:hover': {
                  boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.12)}`,
                },
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{
                        p: 1.2,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.secondary.main, 0.15),
                        color: 'secondary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <StarIcon sx={{ fontSize: 26 }} />
                      </Box>
                      <Typography variant="h6" fontWeight={700}>
                        Reviews ({selectedUserActivity.reviews.total})
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip 
                        label={`${selectedUserActivity.reviews.verified} Verified`} 
                        size="small" 
                        sx={{ 
                          fontWeight: 600,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: 'primary.main',
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                        }}
                      />
                      {selectedUserActivity.reviews.hidden > 0 && (
                        <Chip 
                          label={`${selectedUserActivity.reviews.hidden} Hidden`} 
                          size="small" 
                          sx={{ 
                            fontWeight: 600,
                            bgcolor: alpha(theme.palette.warning.main, 0.1),
                            color: 'warning.dark',
                            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                  
                  {selectedUserActivity.reviews.list.length === 0 ? (
                    <Paper sx={{ 
                      p: 4, 
                      textAlign: 'center', 
                      bgcolor: 'grey.50',
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    }}>
                      <StarIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                      <Typography variant="body1" color="text.secondary" fontWeight={500}>
                        No reviews yet
                      </Typography>
                    </Paper>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {selectedUserActivity.reviews.list.map((review, index) => (
                        <Paper 
                          key={review._id} 
                          sx={{ 
                            p: 3, 
                            borderRadius: 3,
                            border: `1px solid ${review.is_hidden 
                              ? alpha(theme.palette.grey[400], 0.5) 
                              : alpha(theme.palette.divider, 0.5)}`,
                            bgcolor: review.is_hidden ? alpha(theme.palette.grey[200], 0.5) : 'white',
                            boxShadow: `0 2px 4px ${alpha(theme.palette.common.black, 0.06)}`,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            animation: `slideInUp 0.4s ease-out ${0.5 + index * 0.1}s both`,
                            '&:hover': {
                              boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.12)}`,
                              transform: 'translateY(-2px)',
                              borderColor: review.is_hidden 
                                ? alpha(theme.palette.grey[500], 0.7) 
                                : alpha(theme.palette.primary.main, 0.3),
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                            <Box>
                              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                                {review.creator_info?.username || 'Unknown Creator'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                {review.createdAt 
                                  ? new Date(review.createdAt).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })
                                  : 'N/A'}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {review.verified && (
                                <Chip 
                                  label="Verified" 
                                  size="small" 
                                  sx={{ 
                                    height: 24, 
                                    fontWeight: 600,
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                    color: 'primary.main',
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                  }}
                                />
                              )}
                              {review.is_hidden && (
                                <Chip 
                                  label="Hidden" 
                                  size="small" 
                                  sx={{ 
                                    height: 24, 
                                    fontWeight: 600,
                                    bgcolor: alpha(theme.palette.grey[400], 0.1),
                                    color: 'grey.700',
                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              mb: 2, 
                              whiteSpace: 'pre-line',
                              lineHeight: 1.7,
                              color: 'text.primary',
                            }}
                          >
                            {review.fullReview}
                          </Typography>
                          <Box sx={{ 
                            display: 'flex', 
                            gap: 2, 
                            flexWrap: 'wrap',
                            pt: 2,
                            borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                          }}>
                            <Chip 
                              label={`Quality: ${review.contentQuality}/5`}
                              size="small"
                              sx={{ 
                                fontWeight: 500,
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                color: 'text.primary',
                              }}
                            />
                            <Chip 
                              label={`Recommend: ${review.recommend ? 'Yes' : 'No'}`}
                              size="small"
                              sx={{ 
                                fontWeight: 500,
                                bgcolor: review.recommend 
                                  ? alpha(theme.palette.primary.main, 0.1)
                                  : alpha(theme.palette.grey[400], 0.1),
                                color: review.recommend ? 'primary.main' : 'grey.700',
                              }}
                            />
                            {review.spent && (
                              <Chip 
                                label={`Spent: $${review.spent}`}
                                size="small"
                                sx={{ 
                                  fontWeight: 500,
                                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                                  color: 'text.primary',
                                }}
                              />
                            )}
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Alert severity="error">Failed to load user activity</Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 2.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`, bgcolor: 'white' }}>
          <Button
            onClick={() => {
              setUserActivityDialogOpen(false);
              setSelectedUserActivity(null);
            }}
            variant="contained"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Security Log Reviews Dialog */}
      <Dialog
        open={securityLogReviewsDialogOpen}
        onClose={() => {
          setSecurityLogReviewsDialogOpen(false);
          setSelectedSecurityLogReviews(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon color="primary" />
            <Typography variant="h6">
              Reviews from {selectedSecurityLogReviews?.user?.username || 'User'}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {selectedSecurityLogReviews?.details || ''}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {selectedSecurityLogReviews?.reviews && selectedSecurityLogReviews.reviews.length > 0 ? (
            <Stack spacing={2}>
              {selectedSecurityLogReviews.reviews.map((review) => (
                <Card key={review._id} sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar
                        src={review.creator?.profilePicture || '/default-profile.png'}
                        alt={review.creator?.username || 'Creator'}
                        sx={{ width: 48, height: 48 }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" fontWeight={600}>
                          {review.creator?.username || 'Unknown Creator'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {review.createdAt 
                            ? (() => {
                                // Parse ISO string - if it doesn't have timezone, treat as UTC
                                let dateStr = review.createdAt;
                                if (dateStr && !dateStr.includes('Z') && !dateStr.includes('+') && !dateStr.includes('-', 10)) {
                                  // No timezone info, assume UTC
                                  dateStr = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
                                }
                                const date = new Date(dateStr);
                                return new Intl.DateTimeFormat(undefined, {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  timeZoneName: 'short'
                                }).format(date);
                              })()
                            : 'N/A'}
                        </Typography>
                      </Box>
                      <Chip 
                        label={`Quality: ${review.contentQuality}/5`}
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                    {review.fullReview && (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: 'text.primary' }}>
                        {review.fullReview}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Stack>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No reviews found for this log entry
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setSecurityLogReviewsDialogOpen(false);
              setSelectedSecurityLogReviews(null);
            }}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
